import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react'
import { timelineService } from '@/services/timeline';
import { TimelinePost } from '@/types/post';
import { useToast } from '@/hooks/use-toast'
import { buildTimelineParams, TimelineFilters } from '@/utils/timeline'
import { InfiniteScrollContainer } from '@/components/InfiniteScrollContainer'

export interface TimelineContextType {
  posts: TimelinePost[]
  isLoading: boolean
  isFetchingMore: boolean
  hasMore: boolean
  offset: number
  limit: number
  filters: TimelineFilters
  setFilters: (_filters: TimelineFilters) => void
  removePost: (_postId: string) => void
  updatePost: (_postId: string, _changes: Partial<TimelinePost>) => void
  refetch: () => Promise<void>
}

interface TimelinePostContextType {
  post: TimelinePost
  isApproving: boolean
  isRejecting: boolean
  isPinning: boolean
  isFavoriting: boolean
  handleApprove: () => Promise<void>
  handleReject: () => Promise<void>
  handlePin: () => Promise<void>
  handleFavorite: () => Promise<void>
}

export const TimelineContext = createContext<TimelineContextType | undefined>(undefined)
const TimelinePostContext = createContext<TimelinePostContextType | undefined>(undefined)

interface TimelineProviderProps {
  children: ReactNode
  type?: 'APPROVED' | 'REJECTED'
  limit?: number
  filters?: TimelineFilters
}

interface TimelinePostProviderProps {
  children: ReactNode
  post: TimelinePost
}

export function TimelineProvider({ 
  children, 
  type,
  limit = 30,
  filters: initialFilters
}: TimelineProviderProps) {
  const defaultFilters: TimelineFilters = {
    search: '',
    territories: '',
    contexts: '',
    topics: '',
    partners: '',
    sources: '',
    favorited: undefined,
    created_by: undefined,
  }
  const [posts, setPosts] = useState<TimelinePost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const [filters, setFilters] = useState<TimelineFilters>(initialFilters || defaultFilters)
  const { toast } = useToast()

  const fetchPosts = useCallback(async (fetchOffset = 0, reset = false) => {
    try {
      if (reset) {
        setIsLoading(true)
      } else if (fetchOffset > 0) {
        setIsFetchingMore(true)
      } else {
        setIsLoading(true)
      }
      
      const params = buildTimelineParams({
        offset: fetchOffset,
        limit,
        type,
        ...filters,
      })

      const response = await timelineService.getAll(params)
      
      if (reset) {
        setPosts(response.data)
      } else {
        setPosts(prev => [...prev, ...response.data])
      }
      
      setHasMore(response.remaining > 0)
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Erro ao carregar posts',
        description: 'Não foi possível carregar os posts. Tente novamente.',
      })
      console.error('Error fetching posts:', error)
    } finally {
      if (reset || fetchOffset === 0) {
        setIsLoading(false)
      }
      if (fetchOffset > 0) {
        setIsFetchingMore(false)
      }
    }
  }, [limit, type, filters, toast])

  const refetch = async () => {
    setOffset(0)
    setHasMore(true)
    await fetchPosts(0, true)
  }

  const removePost = (postId: string) => {
    setPosts(prev => prev.filter(post => post.id !== postId))
  }

  const updatePost = (postId: string, changes: Partial<TimelinePost>) => {
    setPosts(prev => prev.map(post => post.id === postId ? { ...post, ...changes } : post))
  }

  const handleLoadMore = useCallback(() => {
    const newOffset = offset + limit
    setOffset(newOffset)
    fetchPosts(newOffset)
  }, [offset, limit, fetchPosts])

  useEffect(() => {
    setOffset(0)
    setHasMore(true)
    fetchPosts(0, true)
  }, [type, filters, fetchPosts])

  useEffect(() => {
    if (initialFilters) {
      setFilters(initialFilters)
    }
  }, [initialFilters])

  return (
    <TimelineContext.Provider 
      value={{ 
        posts, 
        isLoading, 
        isFetchingMore,
        hasMore, 
        offset, 
        limit, 
        filters, 
        setFilters, 
        removePost, 
        updatePost,
        refetch 
      }}
    >
      <InfiniteScrollContainer
        hasMore={hasMore}
        isLoading={isLoading}
        isFetchingMore={isFetchingMore}
        onLoadMore={handleLoadMore}
      >
        {children}
      </InfiniteScrollContainer>
    </TimelineContext.Provider>
  )
}

export function TimelinePostProvider({ 
  children, 
  post
}: TimelinePostProviderProps) {
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isPinning, setIsPinning] = useState(false)
  const [isFavoriting, setIsFavoriting] = useState(false)
  const { toast } = useToast()
  const { removePost, updatePost } = useTimeline()

  const handleApprove = async () => {
    setIsApproving(true)
    try {
      await timelineService.approve(post.id)
      removePost(post.id)
      toast({
        title: 'Post aprovado',
        description: 'O post foi aprovado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro ao aprovar',
        description: 'Não foi possível aprovar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async () => {
    setIsRejecting(true)
    try {
      await timelineService.reject(post.id)
      removePost(post.id)
      toast({
        title: 'Post rejeitado',
        description: 'O post foi rejeitado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro ao rejeitar',
        description: 'Não foi possível rejeitar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsRejecting(false)
    }
  }

  const handlePin = async () => {
    setIsPinning(true)
    try {
      await timelineService.pin(post.id)
      updatePost(post.id, { is_pinned: !post.is_pinned })
      toast({
        title: post.is_pinned ? 'Post desfixado' : 'Post fixado',
        description: post.is_pinned ? 'O post foi desfixado com sucesso.' : 'O post foi fixado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro ao fixar post',
        description: 'Não foi possível fixar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsPinning(false)
    }
  }

  const handleFavorite = async () => {
    setIsFavoriting(true)
    try {
      await timelineService.favorite(post.id)
      updatePost(post.id, { is_favorited: !post.is_favorited })
      toast({
        title: post.is_favorited ? 'Removido dos favoritos' : 'Adicionado aos favoritos',
        description: post.is_favorited ? 'O post foi removido dos favoritos.' : 'O post foi adicionado aos favoritos.',
      })
    } catch {
      toast({
        title: 'Erro ao favoritar post',
        description: 'Não foi possível favoritar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsFavoriting(false)
    }
  }

  return (
    <TimelinePostContext.Provider 
      value={{ 
        post, 
        isApproving, 
        isRejecting,
        isPinning,
        isFavoriting,
        handleApprove,
        handleReject,
        handlePin,
        handleFavorite
      }}
    >
      {children}
    </TimelinePostContext.Provider>
  )
}

export function useTimeline() {
  const context = useContext(TimelineContext)
  if (context === undefined) {
    throw new Error('useTimeline must be used within a TimelineProvider')
  }
  return context
}

export function useTimelinePost() {
  const context = useContext(TimelinePostContext)
  if (context === undefined) {
    throw new Error('useTimelinePost must be used within a TimelinePostProvider')
  }
  return context
}

